import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { FranchiseBadge } from "../components/FranchiseBadge.js";
import { Button, Notice } from "../components/ui.js";
import {
  auctionLotLabel,
  captainRaise,
  fetchCaptainState,
  playerFacts,
  raiseBid,
} from "../lib/auction.js";
import { useAuth } from "../lib/auth.js";
import type { AuctionCurrency } from "../lib/currency.js";
import { captainsIn, fetchFranchises, type FranchiseSetup } from "../lib/franchises.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { AuctionCaptainState, Tournament, TournamentEvent } from "../lib/types.js";
import { FranchiseHeading, TeamCard, usePeople, type People } from "./auction/parts.js";
import { LotsLinkRow } from "./TournamentLots.js";

/**
 * How often a captain's phone asks again: the board's pace. A raise against a bid that has moved
 * since is refused by the database, so the lag costs a tap, never a price nobody saw.
 */
const CAPTAIN_REFRESH_MS = 2000;

const captainStateKey = (eventId: string) => ["auction-captain", eventId] as const;

/**
 * A captain's phone in the auction room: the board for the pool they captain while it runs,
 * the most their squad can still bid (theirs alone to see), and a button to raise for it. The
 * auctioneer still puts players up and calls Sold. Anybody else is told there is nothing here;
 * the database sends them nothing either.
 */
export function AuctionCaptain() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { data: tournaments, isPending } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments?.find((t) => t.id === tournamentId);

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/tournaments/${tournamentId}`} className="micro mb-4 inline-block underline">
        ← {tournament?.name ?? "Tournament"}
      </Link>
      {isPending ? (
        <p className="micro">Loading…</p>
      ) : !tournament || !tournament.auction ? (
        <Notice tone="attention">That tournament has no auction.</Notice>
      ) : (
        <CaptainScreen tournament={tournament} />
      )}
    </main>
  );
}

function CaptainScreen({ tournament }: { tournament: Tournament }) {
  const { member } = useAuth();
  const { data: setup } = useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
  });
  const captains = tournament.events.filter((event) =>
    setup?.squads.some((s) => s.eventId === event.id && s.captain?.id === member?.id),
  );
  const states = useQueries({
    queries: captains.map((event) => ({
      queryKey: captainStateKey(event.id),
      queryFn: () => fetchCaptainState(event.id),
      refetchInterval: event.auctionFinishedAt === null ? CAPTAIN_REFRESH_MS : (false as const),
    })),
  });
  const index = states.findIndex((s) => s.data?.open);
  const state = index >= 0 ? states[index]!.data! : null;
  const event = index >= 0 ? captains[index]! : null;

  if (!setup) return <p className="micro">Loading…</p>;
  if (captains.length === 0) {
    return (
      <>
        <PageHeader eyebrow={tournament.name} title="Bid" />
        <p className="text-sm text-ink-muted">You don't captain a team in this auction.</p>
      </>
    );
  }
  if (tournament.auctionStartedAt === null) {
    return (
      <>
        <PageHeader eyebrow={tournament.name} title="Bid" />
        <p className="text-sm text-ink-muted">The auction hasn't started yet.</p>
        <CaptainLots tournament={tournament} events={captains} />
      </>
    );
  }
  if (!state || !event) {
    return (
      <>
        <PageHeader eyebrow={tournament.name} title="Bid" />
        <p className="text-sm text-ink-muted">
          No pool you captain is open. This page follows the room as soon as one is.
        </p>
        <CaptainLots tournament={tournament} events={captains} />
      </>
    );
  }

  return (
    <Live
      tournament={tournament}
      event={event}
      state={state}
      setup={setup}
      currency={tournament.auctionCurrency}
    />
  );
}

function Live({
  tournament,
  event,
  state,
  setup,
  currency,
}: {
  tournament: Tournament;
  event: TournamentEvent;
  state: AuctionCaptainState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
}) {
  const people = usePeople(event, setup, currency);
  const mine = state.squads.find((s) => s.squadId === state.mine.squadId);
  // My squad first: it is the one a captain reads most.
  const squads = [...state.squads].sort(
    (a, b) => Number(b.squadId === state.mine.squadId) - Number(a.squadId === state.mine.squadId),
  );
  const pool = { fill: "auction" as const, players: state.players };

  return (
    <>
      <PageHeader eyebrow={`${tournament.name} · ${event.name}`} title="Bid" />
      <div className="mb-4">
        <LotsLinkRow tournament={tournament} event={event} />
      </div>
      {state.up ? (
        <Stage event={event} state={state} people={people} />
      ) : (
        <p className="mb-6 border-2 p-4 text-sm" style={{ borderColor: "var(--color-line)" }}>
          Waiting for the next player.
        </p>
      )}

      {mine ? (
        <section
          className="mb-8 border-2 p-4"
          style={{
            borderColor: "var(--color-line)",
            borderTop: `4px solid ${people.franchise(mine.franchiseId)?.colour ?? "var(--color-line)"}`,
          }}
          aria-label="Your team"
        >
          <FranchiseHeading
            franchise={people.franchise(mine.franchiseId)}
            name={people.franchiseName(mine.franchiseId)}
            aside={<span className="micro">your team</span>}
          />
          <dl className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <dt className="micro">Purse left</dt>
              <dd className="font-display text-3xl leading-none" aria-label="Your purse left">
                {people.price(mine.left)}
              </dd>
            </div>
            <div>
              <dt className="micro">Max bid · only you see this</dt>
              <dd className="font-display text-3xl leading-none" aria-label="Your max bid">
                {state.mine.maxBid === null ? "—" : people.price(state.mine.maxBid)}
              </dd>
            </div>
          </dl>
        </section>
      ) : null}

      <h2 className="micro mb-3">Every team</h2>
      <div className="grid gap-4">
        {squads.map((squad) => (
          <TeamCard key={squad.squadId} squad={squad} state={pool} people={people} />
        ))}
      </div>
    </>
  );
}

/** The player up, the bid and who holds it, and the captain's one control: raise. */
function Stage({
  event,
  state,
  people,
}: {
  event: TournamentEvent;
  state: AuctionCaptainState;
  people: People;
}) {
  const queryClient = useQueryClient();
  const up = state.up!;
  const who = people.person(up.memberId);
  const lot = state.lots.find((l) => l.lotId === up.lotId);
  const holder = state.squads.find((s) => s.squadId === up.holderId);
  const holderFranchise = holder ? people.franchise(holder.franchiseId) : undefined;
  const action = captainRaise(state);

  const raise = useMutation({
    mutationFn: () =>
      raiseBid(event.id, state.mine.squadId, {
        memberId: up.memberId,
        bid: up.bid,
      }),
    // Refused or not, ask again at once: the new bid is what the captain needs to see next.
    onSettled: () => queryClient.invalidateQueries({ queryKey: captainStateKey(event.id) }),
  });
  // A refusal belongs to the player it was about; the next one starts clean.
  const { reset } = raise;
  useEffect(() => reset(), [up.memberId, reset]);

  return (
    <section
      className="mb-6 border-2"
      style={{ borderColor: "var(--color-line)" }}
      aria-label="Up now"
    >
      <div className="flex items-center gap-4 p-4">
        <Avatar name={who.name} url={who.avatarUrl} size={64} />
        <div className="min-w-0">
          <p className="micro">
            Up now
            {up.offers > 0 ? " · offered again" : ""}
            {lot ? ` · ${auctionLotLabel(lot)} · base ${people.price(lot.basePrice ?? 0)}` : ""}
          </p>
          <p className="font-display break-words text-3xl leading-[0.95]">{who.name}</p>
          {playerFacts(up.details).length > 0 ? (
            <p className="mt-1 font-mono text-[11px] text-ink-muted">
              {playerFacts(up.details).join(" · ")}
            </p>
          ) : null}
        </div>
      </div>

      <div
        className="flex items-end justify-between gap-3 border-t-2 p-4"
        style={{ borderColor: "var(--color-line)" }}
        aria-live="polite"
      >
        <div className="shrink-0">
          <p className="micro">{up.bid === null ? "Opening bid" : "Current bid"}</p>
          <p
            className="font-display whitespace-nowrap text-5xl leading-none"
            aria-label="Current bid"
          >
            <span className={up.bid === null ? "" : "highlight px-2"}>
              {people.price(up.bid ?? up.nextBid)}
            </span>
          </p>
        </div>
        <p
          className="flex min-w-0 items-center justify-end gap-2 text-right text-sm font-semibold"
          aria-label="Held by"
        >
          {holder ? (
            <>
              {holderFranchise ? <FranchiseBadge franchise={holderFranchise} size={22} /> : null}
              {people.franchiseName(holder.franchiseId)}
            </>
          ) : (
            <span className="text-ink-muted">No bid yet</span>
          )}
        </p>
      </div>
      {up.forced && up.bid === null ? (
        <p className="micro px-4 pb-3" aria-label="Forced player">
          Only {up.forced.name} can take this player
        </p>
      ) : null}

      <div className="border-t-2 p-4" style={{ borderColor: "var(--color-line)" }}>
        {raise.error ? <Notice tone="attention">{raise.error.message}</Notice> : null}
        {action?.kind === "raise" ? (
          <Button
            tone="primary"
            className="w-full"
            style={{ minHeight: 64, fontSize: 20 }}
            disabled={raise.isPending}
            onClick={() => raise.mutate()}
          >
            Raise to {people.price(action.price)}
          </Button>
        ) : (
          <p className="text-center text-sm font-semibold" aria-label="Why you can't raise">
            {action?.kind === "holds"
              ? "You hold the bid"
              : action?.kind === "over"
                ? `You can bid at most ${people.price(action.max)}`
                : action?.kind === "refused"
                  ? action.reason
                  : null}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * The lots of each pool the member captains, while none of them is open. Each row hides itself
 * until its pool's lots are published.
 */
function CaptainLots({
  tournament,
  events,
}: {
  tournament: Tournament;
  events: TournamentEvent[];
}) {
  return (
    <div className="mt-4">
      {events.map((event) => (
        <LotsLinkRow key={event.id} tournament={tournament} event={event} />
      ))}
    </div>
  );
}

/**
 * The tournament page's way to the captain's screen: shown to a member who captains a team in
 * a pool not yet finished, once the auction has started.
 */
export function CaptainBidLink({ tournament }: { tournament: Tournament }) {
  const { member } = useAuth();
  const { data: setup } = useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
    enabled: tournament.auction && tournament.auctionStartedAt !== null,
  });
  if (!tournament.auction || tournament.auctionStartedAt === null || !setup || !member) return null;
  const open = setup.squads.filter((s) =>
    tournament.events.some((e) => e.id === s.eventId && e.auctionFinishedAt === null),
  );
  const captains = captainsIn({ ...setup, squads: open }, member.id);
  if (!captains) return null;

  return (
    <Link
      to={`/tournaments/${tournament.id}/bid`}
      className="mt-4 flex min-h-[56px] items-center justify-between border-2 px-4 text-base font-bold"
      style={{ borderColor: "var(--color-line)" }}
    >
      Bid in the auction
      <span className="micro">your team&apos;s phone</span>
    </Link>
  );
}
